import { UpdateEmploymentStatusSubmission } from './update-employment-status-submission';
import { Deserializable } from '../../protocols/deserializable';
import { SubmissionStatusIdEnum } from '../../enum/shared/submission-status-id.enum';
import { MemberSubmissionStatus } from '../member-submission-status';
import { SubmissionSubStatusIdEnum } from '../../enum/shared/submission-sub-status-id-enum';
import { SubmissionSubStatus } from '../submission-sub-status';
import { SubmissionComment } from '../submission-comment';
import { SubmissionHistoryItem } from '../submission-history-item';
import { SubmissionReminder, SubmissionStakeholder } from '../member-submission';
import { PortalTypeIdEnum } from '../../enum/shared/portal-type-id.enum';
import { SubmissionAttachment } from '../submission-attachment';
import { NewEmployerSubmission } from './new-employer-submission';
import { EmployerSubmissionTypeEnum } from '../../users/enum/employer-submission-type.enum';
import { SubmissionStatusPill } from '../../shared/submission-status-pill';
import * as moment from 'moment';
import { UpdateEmployerAddressSubmission } from './update-employer-address-submission';
import { UpdateEmployerContactsSubmission } from './update-employer-contacts-submission';

export class IpEmployerSubmission implements Deserializable {
  public id!: number;
  public employerId!: number;
  public employerName!: string;
  public submissionTypeId!: EmployerSubmissionTypeEnum;
  public submissionTypeName!: string;
  public statusId!: SubmissionStatusIdEnum;
  public status!: MemberSubmissionStatus;
  public subStatusId!: SubmissionSubStatusIdEnum | null;
  public subStatus!: SubmissionSubStatus;
  public comments!: SubmissionComment[];
  public ownerId!: number | null;
  public owner!: SubmissionStakeholder;
  public approverId!: number | null;
  public approver!: SubmissionStakeholder;
  public history!: SubmissionHistoryItem[];
  public idPrefix!: string;
  public idSuffix!: string;
  public submittedDate!: string;
  public overdueDate!: string;
  public reminder!: SubmissionReminder | null;
  public expirationDate!: string;
  public lastStatusUpdateDate!: string;
  public createdInPortalTypeId!: PortalTypeIdEnum;
  public completedDate!: string | null;
  public createdDate!: string | null;
  public attachments!: SubmissionAttachment[];
  public newEmployerSubmissionId!: string;
  public newEmployerSubmission!: NewEmployerSubmission;
  public updateAddressSubmissionId!: string;
  public updateEmploymentStatusSubmission!: UpdateEmploymentStatusSubmission;
  public updateAddressSubmission!: UpdateEmployerAddressSubmission;
  public updateContactsSubmissionId!: string;
  public updateContactsSubmission!: UpdateEmployerContactsSubmission;

  // Not From API
  public submissionId!: string;
  public formattedExpirationDate!: string;
  public expiringSoon!: boolean;
  public statusPill!: SubmissionStatusPill;
  public completedDateString!: string;
  public createdDateString!: string;

  public canUploadFiles(): boolean {
    return this.statusId === SubmissionStatusIdEnum.Submitted || this.statusId === SubmissionStatusIdEnum.InReview;
  }

  public canChangeSubmissionOwner(): boolean {
    return (
      this.subStatusId === SubmissionSubStatusIdEnum.Ready ||
      this.subStatusId === SubmissionSubStatusIdEnum.PendingDocuments ||
      this.subStatusId === SubmissionSubStatusIdEnum.Processing ||
      this.subStatusId === SubmissionSubStatusIdEnum.MemberActionRequired ||
      this.subStatusId === SubmissionSubStatusIdEnum.EmployerActionRequired ||
      this.subStatusId === SubmissionSubStatusIdEnum.ReadyForApproval
    );
  }

  public canChangeSubmissionApprover(): boolean {
    return this.subStatusId === SubmissionSubStatusIdEnum.ReadyForApproval;
  }

  public isApproved(): boolean {
    // @ts-ignore
    return [SubmissionSubStatusIdEnum.Approved, SubmissionSubStatusIdEnum.AutoApproved].contains(this.subStatusId ?? 0);
  }

  public isRejected(): boolean {
    return this.subStatusId === SubmissionSubStatusIdEnum.Rejected;
  }

  public onDeserialize() {
    const Deserialize = window?.injector?.Deserialize;
    this.submissionId = this.buildSubmissionId();

    if (!!this.owner) {
      this.owner = Deserialize?.instanceOf(SubmissionStakeholder, this.owner);
    }
    if (!!this.approver) {
      this.approver = Deserialize?.instanceOf(SubmissionStakeholder, this.approver);
    }
    if (!!this.comments) {
      this.comments = Deserialize?.arrayOf(SubmissionComment, this.comments);
    }
    if (!!this.reminder) {
      this.reminder = Deserialize?.instanceOf(SubmissionReminder, this.reminder);
    }
    if (!!this.history) {
      this.history = Deserialize?.arrayOf(SubmissionHistoryItem, this.history);
      this.history.sort((a, b) => (b.eventDate ?? '').localeCompare(a.eventDate ?? ''));
    }
    if (!!this.status) {
      this.status = Deserialize?.instanceOf(MemberSubmissionStatus, this.status);
      const statusName = this.isRejected() ? $localize`Rejected` : this.status.name;
      this.statusPill = new SubmissionStatusPill(this.id, statusName, this.statusId, this.status.name);
    }
    if (!!this.subStatus) {
      this.subStatus = Deserialize?.instanceOf(SubmissionSubStatus, this.subStatus);
    }
    if (!!this.attachments) {
      this.attachments = Deserialize?.arrayOf(SubmissionAttachment, this.attachments);
    }
    if (!!this.newEmployerSubmission) {
      this.newEmployerSubmission = Deserialize?.instanceOf(NewEmployerSubmission, this.newEmployerSubmission);
    }

    if (!!this.updateEmploymentStatusSubmission) {
      this.updateEmploymentStatusSubmission = Deserialize?.instanceOf(
        UpdateEmploymentStatusSubmission,
        this.updateEmploymentStatusSubmission
      );
    }

    if (!!this.updateAddressSubmission) {
      this.updateAddressSubmission = Deserialize?.instanceOf(
        UpdateEmployerAddressSubmission,
        this.updateAddressSubmission
      );
    }

    if (!!this.expirationDate) {
      this.formattedExpirationDate = moment(this.expirationDate).format('MMMM D, YYYY');
      // if the submission is expiring within the next 10 days, set expiringSoon to true
      this.expiringSoon = moment(this.expirationDate).diff(moment(), 'days') <= 10;
    }

    if (!!this.completedDate) {
      this.completedDateString = this.completedDate;
    }

    if (!!this.createdDate) {
      this.createdDateString = this.createdDate;
    }
  }

  private buildSubmissionId(): string {
    // Submission IDs should be 8 characters long, padded with leading zeros
    const paddedId = this.id.toString().padStart(8, '0');
    return `${this.idPrefix}-${paddedId}-${this.idSuffix}`;
  }
}
